/** Global watcher that fires a desktop notification + chime whenever a
 * trade opens or closes. Polls ALL of today's trades every 5s (its own
 * query key) and diffs the reason so an exit says WHY (SL / target / manual).
 *
 * When `active` is false the query is disabled and the snapshot is reset,
 * so turning alerts on mid-session re-seeds cleanly (no back-dated flood). */

import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { tradeLogApi } from "@/lib/tradeLogApi";
import { ALL_STRATEGIES, type TradeLogEntry } from "@/types/tradeLog";
import { playAlarm, playChime, showDesktopNotification, showHardNotification } from "@/lib/notify";
import { getStrategySound } from "@/lib/soundPrefs";
import { computeTradeEvents, describeTradeEvent, soundEventOf } from "@/lib/tradeEvents";

const REFETCH_MS = 5_000;

export function useTradeNotifications(active: boolean): void {
  const prevRef = useRef<Map<string, TradeLogEntry> | null>(null);

  const q = useQuery({
    // Own key (not the OpenPositionSection "open-positions" one): we need ALL
    // of today's trades so an exit carries its reason, not just the open ones.
    queryKey: ["trade-alerts", "all"] as const,
    queryFn: async () => {
      // LIVE + PAPER merged: during a paper-test phase the operator wants
      // the same entry/exit chime for a paper fill as for real money (the
      // toast itself carries a [PAPER] tag). The backend never mixes the
      // two in one call, so fetch both.
      const base = { range: "today", strategyId: ALL_STRATEGIES, show: "trades" } as const;
      const [live, paper] = await Promise.all([
        tradeLogApi.list({ ...base }),
        tradeLogApi.list({ ...base, executionMode: "PAPER" }),
      ]);
      return [...live.trades, ...paper.trades];
    },
    refetchInterval: REFETCH_MS,
    // Keep polling in the BACKGROUND so entry/exit desktop alerts fire while
    // the operator is on their broker/TV chart (React Query pauses interval
    // refetch when unfocused by default, which was swallowing exit alerts).
    refetchIntervalInBackground: true,
    enabled: active,
  });

  const data = q.data;
  useEffect(() => {
    if (!active) {
      prevRef.current = null;
      return;
    }
    if (data === undefined) return;
    const { events, next } = computeTradeEvents(prevRef.current, data);
    prevRef.current = next;
    for (const event of events) {
      const { title, body } = describeTradeEvent(event);
      // An EXIT that FAILED is an emergency (the position may still be open at
      // the broker): sticky notification + a repeating siren, not the normal
      // fade-away toast + soft chime.
      if (event.kind === "exit" && event.trade.exitReason === "EXIT_FAILED") {
        showHardNotification(title, body, `nss-exit-failed-${event.trade.id}`);
        playAlarm();
      } else {
        showDesktopNotification(title, body);
        playChime(
          event.kind,
          getStrategySound(event.trade.strategyId, soundEventOf(event), event.trade.executionMode),
        );
      }
    }
  }, [active, data]);
}
